import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';

interface FogStatus {
  severity: string;
  color: string;
  title: string;
  message: string;
  airports?: string[];
}

interface DataQualityWarning {
  type: 'error' | 'warning';
  title: string;
  message: string;
}

interface StatusAlertProps {
  fogStatus?: FogStatus;
  hasDataError?: boolean;
  dataQualityWarning?: DataQualityWarning | null;
  isLoading?: boolean;
}

const StatusAlert: React.FC<StatusAlertProps> = ({ fogStatus, hasDataError = false, dataQualityWarning = null, isLoading = false }) => {
  // Priority 0: Loading state (initial data fetch)
  if (isLoading) {
    return (
      <Box>
        <Paper
          sx={{
            p: 1.5,
            backgroundColor: 'rgba(117, 117, 117, 0.1)',
            border: '2px solid #757575',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: { xs: 10, sm: 14, lg: 16, xl: 20 },
                height: { xs: 10, sm: 14, lg: 16, xl: 20 },
                borderRadius: '50%',
                backgroundColor: '#757575',
                flexShrink: 0,
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: '1.1rem',
                '@media (min-width: 1920px)': { fontSize: '1.6rem' },
                '@media (min-width: 2560px)': { fontSize: '1.8rem' }
              }}
            >
              Loading Data
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: '#fff',
              ml: { xs: 2.75, sm: 3.25, lg: 3.5, xl: 4 },
              fontSize: '0.875rem',
              '@media (min-width: 1920px)': { fontSize: '1.125rem' },
              '@media (min-width: 2560px)': { fontSize: '1.25rem' }
            }}
          >
            Fetching forecast data...
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Priority 1: Data refresh error (chunk loading failure)
  if (hasDataError) {
    return (
      <Box>
        <Paper
          sx={{
            p: 1.5,
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            border: '2px solid #ff9800',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmberIcon
              sx={{
                color: '#ff9800',
                fontSize: { xs: 20, sm: 24, lg: 28, xl: 32 },
                flexShrink: 0
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: '1.1rem',
                '@media (min-width: 1920px)': { fontSize: '1.6rem' },
                '@media (min-width: 2560px)': { fontSize: '1.8rem' }
              }}
            >
              Data Refresh Issue
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: '#fff',
              ml: { xs: 3, sm: 4, lg: 5, xl: 5.5 },
              fontSize: '0.875rem',
              '@media (min-width: 1920px)': { fontSize: '1.125rem' },
              '@media (min-width: 2560px)': { fontSize: '1.25rem' }
            }}
          >
            Some data did not refresh. Forecast may be inaccurate until the next refresh.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Priority 2: Data quality warnings (incomplete predictions, missing historical data)
  if (dataQualityWarning) {
    const isError = dataQualityWarning.type === 'error';
    const warningColor = isError ? '#f44336' : '#ff9800';
    const backgroundColor = isError ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 152, 0, 0.1)';
    const Icon = isError ? ErrorIcon : WarningAmberIcon;

    return (
      <Box>
        <Paper
          sx={{
            p: 1.5,
            backgroundColor: backgroundColor,
            border: `2px solid ${warningColor}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Icon
              sx={{
                color: warningColor,
                fontSize: { xs: 20, sm: 24, lg: 28, xl: 32 },
                flexShrink: 0
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: '1.1rem',
                '@media (min-width: 1920px)': { fontSize: '1.6rem' },
                '@media (min-width: 2560px)': { fontSize: '1.8rem' }
              }}
            >
              {dataQualityWarning.title}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: '#fff',
              ml: { xs: 3, sm: 4, lg: 5, xl: 5.5 },
              fontSize: '0.875rem',
              '@media (min-width: 1920px)': { fontSize: '1.125rem' },
              '@media (min-width: 2560px)': { fontSize: '1.25rem' }
            }}
          >
            {dataQualityWarning.message}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Priority 3: Fog status summary
  if (!fogStatus) {
    return null;
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 1.5,
          backgroundColor: fogStatus.severity === 'none'
            ? 'rgba(76, 175, 80, 0.1)'
            : fogStatus.severity === 'nodata'
            ? 'rgba(117, 117, 117, 0.1)'
            : `${fogStatus.color}20`,
          border: `2px solid ${fogStatus.color}`,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: { xs: 10, sm: 14, lg: 16, xl: 20 },
              height: { xs: 10, sm: 14, lg: 16, xl: 20 },
              borderRadius: '50%',
              backgroundColor: fogStatus.color,
              flexShrink: 0,
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: '#fff',
              fontWeight: 600,
              fontSize: '1.1rem',
              '@media (min-width: 1920px)': { fontSize: '1.6rem' },
              '@media (min-width: 2560px)': { fontSize: '1.8rem' }
            }}
          >
            {fogStatus.title}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: fogStatus.severity === 'none' ? '#aaa' : '#fff',
            ml: { xs: 2.75, sm: 3.25, lg: 3.5, xl: 4 },
            fontSize: '0.875rem',
            '@media (min-width: 1920px)': { fontSize: '1.125rem' },
            '@media (min-width: 2560px)': { fontSize: '1.25rem' }
          }}
        >
          {fogStatus.message}
        </Typography>
      </Paper>
    </Box>
  );
};

export default StatusAlert;